import { useState, useEffect } from "react";
import styled from "styled-components";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatLineSpacingIcon from "@mui/icons-material/FormatLineSpacing";
import FontDownloadIcon from "@mui/icons-material/FontDownload";

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.sm};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.veryDarkGrey};
`;

const BlockGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const MenuBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.veryLightGrey};
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primaryColor};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const BlockIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.primaryColor};
  color: #fff;
  font-size: 1.25rem;
`;

const BlockLabel = styled.div`
  font-size: ${({ theme }) => theme.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.fontColor};
`;

const OptionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const OptionButton = styled.button`
  flex: 1;
  padding: 0.625rem;
  font-size: ${({ theme }) => theme.sm};
  font-weight: 500;
  color: ${({ selected, theme }) => selected ? '#fff' : (theme.fontColor)};
  background-color: ${({ selected, theme }) => selected ? (theme.primaryColor) : '#fff'};
  border: 1px solid ${({ selected, theme }) => selected ? (theme.primaryColor) : (theme.veryLightGrey)};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primaryColor};
    background-color: ${({ selected, theme }) => selected ? (theme.primaryColor) : '#f8f9fa'};
  }
`;

function TextCustomizationSection({ assetUrls, textSize, setTextSize, spacing, setSpacing, font, setFont }) {

  const defaultFontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  const textSizeOptions = [
    { label: 'Small', value: 16 },
    { label: 'Medium', value: 18 },
    { label: 'Large', value: 20 }
  ];

  const spacingOptions = [
    { label: 'Normal', value: 1.5 },
    { label: 'Comfortable', value: 1.7 },
    { label: 'Relaxed', value: 2.0 }
  ];

  const fontOptions = [
    { label: 'Sans', value: defaultFontStack },
    { label: 'Serif', value: 'Georgia, "Times New Roman", Times, serif' },
    { label: 'Dyslexia', value: 'OpenDyslexic, "Comic Sans MS", sans-serif' }
  ];

  const handleOnClick = (setter, value) => () => {
    setter(value);
  };

  const handleKeyDown = (setter, value) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setter(value);
    }
  };


  // Update global CSS variables when settings change
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--a11y-font-size', `${textSize}px`);
    root.style.setProperty('--a11y-line-height', spacing.toString());
    root.style.setProperty('--a11y-font-family', font);
  }, [textSize, spacing, font]);

  return (
    <>
      <SectionTitle id="text-customizations">Text Customization</SectionTitle>

      <BlockGrid>
        <MenuBlock>
          <BlockHeader>
            <BlockIconWrapper aria-hidden="true">
              <TextFieldsIcon sx={{ fontSize: 'inherit' }} />
            </BlockIconWrapper>
            <BlockLabel id="text-size-label">Text Size</BlockLabel>
          </BlockHeader>

          <OptionButtons 
            role="radiogroup"
            aria-labelledby="text-size-label"
          >
            {textSizeOptions.map((option) => {
              const selected = textSize === option.value;
              return (
                <OptionButton
                  key={option.value}
                  role="radio"
                  aria-checked={selected}                      
                  selected={selected}
                  onClick={handleOnClick(setTextSize, option.value)}
                  onKeyDown={handleKeyDown(setTextSize, option.value)}
                >
                  {option.label}
                </OptionButton>
              );
            })}
          </OptionButtons>
        </MenuBlock>

        <MenuBlock>
          <BlockHeader>
            <BlockIconWrapper aria-hidden="true">
              <FormatLineSpacingIcon sx={{ fontSize: 'inherit' }} />
            </BlockIconWrapper>
            <BlockLabel id="line-spacing-label">Line Spacing</BlockLabel>
          </BlockHeader>

          <OptionButtons
            role="radiogroup"
            aria-labelledby="line-spacing-label"
          >
            {spacingOptions.map((option) => {
              const selected = spacing === option.value;
              return (
                <OptionButton
                  key={option.value}
                  role="radio"
                  aria-checked={selected}                      
                  selected={selected}
                  onClick={handleOnClick(setSpacing, option.value)}
                  onKeyDown={handleKeyDown(setSpacing, option.value)}
                >
                  {option.label}
                </OptionButton>
              );
            })}
          </OptionButtons>
        </MenuBlock>

        <MenuBlock>
          <BlockHeader>
            <BlockIconWrapper aria-hidden="true">
              <FontDownloadIcon sx={{ fontSize: 'inherit' }} />
            </BlockIconWrapper>
            <BlockLabel id="font-family-label">Font Family</BlockLabel>
          </BlockHeader>

          <OptionButtons
            role="radiogroup"
            aria-labelledby="font-family-label"
          >
            {fontOptions.map((option) => {
              const selected = font === option.value;
              return (
                <OptionButton
                  key={option.value}
                  role="radio"
                  aria-checked={selected}
                  selected={selected}
                  onClick={handleOnClick(setFont, option.value)}
                  onKeyDown={handleKeyDown(setFont, option.value)}
                >
                  {option.label}
                </OptionButton>
              );
            })}
          </OptionButtons>
        </MenuBlock>
      </BlockGrid>
    </>
  );
}

export default TextCustomizationSection;
